import {ChangeEvent} from 'react';
import styled from 'styled-components';
import {Checkbox} from '@/components/Checkbox';
import {Typography} from '@/components/Typography';
import {COUPON_MESSAGES} from '@/constants';
import {MyCoupon} from '@/interface';
import {Flexbox, FlexFullGrow, interceptProps} from '@/styled-components';
import {palette} from '@/styles';
import {
  getCouponBlockedText,
  getCouponDeadlineText,
  getCouponDeadlineWarning,
  getCouponIssueRouteLabel,
  getCouponPeriodText,
  isCouponDeadlineImminent,
  getCouponDisplayName,
} from '@/utils';

interface IProps {
  coupon: MyCoupon;
  selected: boolean;
  /** 이용 중인 이용권이 있거나 usableNow=false 인 경우 선택 불가 */
  disabled: boolean;
  /** 현재 이용 중인 이용권의 종료일 (등록 만료 경고 판단용) */
  passEndedAt?: string;
  onSelect(coupon: MyCoupon): void;
}

export const CouponListItem = ({
  coupon,
  selected,
  disabled,
  passEndedAt,
  onSelect,
}: IProps) => {
  const imminent = isCouponDeadlineImminent(coupon);
  const blockedText = getCouponBlockedText(coupon);
  const deadlineWarning = getCouponDeadlineWarning(coupon, passEndedAt);

  const handleClick = () => {
    if (!disabled) {
      onSelect(coupon);
    }
  };

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (!disabled && e.target.checked) {
      onSelect(coupon);
    }
  };

  return (
    <Root selected={selected} disabled={disabled} onClick={handleClick}>
      <Flexbox gap="8px" alignItems="flex-start">
        <Checkbox
          checkType="radio"
          variant="small"
          name="my-coupon"
          value={coupon.id}
          checked={selected}
          disabled={disabled}
          onChange={handleChange}
        />
        <ItemBody>
          <Flexbox gap="8px" alignItems="flex-start">
            <RouteBadge>{getCouponIssueRouteLabel(coupon.issueRoute)}</RouteBadge>
            <FlexFullGrow />
            <DeadlineText imminent={imminent}>
              {getCouponDeadlineText(coupon)}
            </DeadlineText>
          </Flexbox>
          <CouponName>{getCouponDisplayName(coupon)}</CouponName>
          <DescriptionList>
            <li>{getCouponPeriodText(coupon)}</li>
            <li>{COUPON_MESSAGES.disappearAfterDeadline}</li>
          </DescriptionList>
          {!!blockedText && <BlockedText>{blockedText}</BlockedText>}
          {!!deadlineWarning && (
            <Typography variant="medium12" color={palette.red100} as="div">
              {deadlineWarning}
            </Typography>
          )}
        </ItemBody>
      </Flexbox>
    </Root>
  );
};

const Root = styled('div').withConfig({
  shouldForwardProp: interceptProps(['selected', 'disabled']),
})<{selected: boolean; disabled: boolean}>`
  border: solid 1px
    ${({selected}) => (selected ? palette.teal100 : palette.gray200)};
  border-radius: 8px;
  padding: 16px;
  cursor: ${({disabled}) => (disabled ? 'default' : 'pointer')};
  background-color: ${({selected, disabled}) =>
    disabled ? palette.gray100 : selected ? palette.primary100 : palette.white};
  ${({disabled}) => disabled && `color: ${palette.gray500};`}
`;

const ItemBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  flex-grow: 1;
  min-width: 0;
`;

const RouteBadge = styled.div`
  color: ${palette.gray600};
  font-size: 12px;
  font-weight: 500;
  line-height: 1.5;
`;

const DeadlineText = styled('div').withConfig({
  shouldForwardProp: interceptProps(['imminent']),
})<{imminent: boolean}>`
  flex-shrink: 0;
  font-size: 12px;
  line-height: 1.5;
  color: ${({imminent}) => (imminent ? palette.red100 : palette.gray500)};
  font-weight: ${({imminent}) => (imminent ? 700 : 500)};
`;

const CouponName = styled.div`
  color: ${palette.gray900};
  font-size: 16px;
  font-weight: 700;
  line-height: 1.5;
`;

const DescriptionList = styled.ul`
  display: flex;
  flex-direction: column;
  gap: 2px;
  margin: 0;
  padding: 0;
  list-style: none;
  color: ${palette.gray600};
  font-size: 13px;
  font-weight: 500;
  line-height: 1.5;
  li::before {
    content: '· ';
  }
`;

const BlockedText = styled.div`
  color: ${palette.red100};
  font-size: 13px;
  font-weight: 700;
  line-height: 1.5;
`;
